import { useEffect, useState } from "react";
import {
  Plus,
  X,
  Edit3,
  Trash2,
  Search,
  Upload,
  Download,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  CheckCircle,
  DollarSign,
} from "lucide-react";
import Swal from "sweetalert2";

import API_BASE_URL from "../config";

export default function PaymentMethodsPage({ setCurrentPage }) {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);

  const [formData, setFormData] = useState({
    paymentMethodName: "",
  });

  const [search, setSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 8;
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  // ---------------- API FUNCTIONS ---------------- //

  const fetchPaymentMethods = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/M_Payment_Method`);
      if (!res.ok) throw new Error("Failed to fetch payment methods");

      const data = await res.json();
      setPaymentMethods(data);
      setFiltered(data);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to fetch payment methods", "error");
    }
  };

  const createPaymentMethod = async () => {
    if (!formData.paymentMethodName.trim()) {
      Swal.fire("Oops!", "Payment Method Name is required", "warning");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}api/M_Payment_Method`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentMethodName: formData.paymentMethodName,
        }),
      });

      if (!res.ok) throw new Error("Failed to create payment method");

      setOpenCreate(false);
      clearForm();
      fetchPaymentMethods();
      Swal.fire("Success!", "Payment Method created successfully", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message, "error");
    }
  };

  const updatePaymentMethod = async () => {
    if (!formData.paymentMethodName.trim()) {
      Swal.fire("Oops!", "Payment Method Name is required", "warning");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}api/M_Payment_Method/${selectedMethod.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: selectedMethod.id,
            paymentMethodName: formData.paymentMethodName,
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to update payment method");

      setOpenEdit(false);
      setSelectedMethod(null);
      clearForm();
      fetchPaymentMethods();
      Swal.fire("Updated!", "Payment Method updated successfully", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message, "error");
    }
  };

  const deletePaymentMethod = async (method) => {
    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `You are about to delete "${method.paymentMethodName}"`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(
          `${API_BASE_URL}api/M_Payment_Method/${method.id}`,
          { method: "DELETE" }
        );
        if (!res.ok) throw new Error("Failed to delete payment method");
        fetchPaymentMethods();
        Swal.fire("Deleted!", "Payment Method has been deleted.", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("Error", err.message, "error");
      }
    }
  };

  // EXPORT
  const exportData = async () => {
    const jsonData = JSON.stringify(paymentMethods, null, 2);
    const blob = new Blob([jsonData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "payment_methods.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  // IMPORT
  const importData = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);

      if (jsonData.length > 0) {
        for (const method of jsonData) {
          await fetch(`${API_BASE_URL}api/M_Payment_Method`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              paymentMethodName: method.paymentMethodName,
            }),
          });
        }
        fetchPaymentMethods();
        Swal.fire(
          "Imported!",
          "Payment Methods imported successfully.",
          "success"
        );
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error!", "Invalid JSON file", "error");
    }
  };

  // ---------------- USE EFFECTS ---------------- //

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(paymentMethods);
    } else {
      const term = search.toLowerCase();
      setFiltered(
        paymentMethods.filter((m) =>
          m.paymentMethodName?.toLowerCase().includes(term)
        )
      );
    }
    setPage(1);
  }, [search, paymentMethods]);

  // ---------------- HELPERS ---------------- //

  const clearForm = () => {
    setFormData({
      paymentMethodName: "",
    });
  };

  const openEditModal = (method) => {
    setSelectedMethod(method);
    setFormData({
      paymentMethodName: method.paymentMethodName,
    });
    setOpenEdit(true);
  };

  // ---------------- RETURN UI ---------------- //
  return (
    <div className="min-h-screen  from-gray-50 to-gray-100 p-8">
      {/* BACK BUTTON */}
      <button
        onClick={() => setCurrentPage("references")}
        className="mb-2 flex items-center gap-2 text-blue-600 hover:text-indigo-800 font-semibold transition-colors group ml-8"
      >
        <ChevronLeft
          size={20}
          className="group-hover:-translate-x-1 transition-transform"
        />
        Back to References
      </button>

      {/* HEADER */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
              <CreditCard className="text-blue-600" size={40} />
              Payment Methods
            </h1>
            <p className="text-gray-600 mt-2">
              Manage payment method options
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportData}
              className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-5 py-3 rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <Download size={18} /> Export
            </button>

            <label className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer">
              <Upload size={18} /> Import
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={importData}
              />
            </label>

            <button
              onClick={() => setOpenCreate(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <Plus size={20} /> Create Payment Method
            </button>
          </div>
        </div>
      </div>

      {/* SEARCH BAR */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search
            size={20}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search payment methods..."
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all shadow-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* TABLE CARD */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <th className="px-6 py-4 text-left text-sm font-semibold">
                  ID
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold">
                  Payment Method Name
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginated.length > 0 ? (
                paginated.map((method, index) => (
                  <tr
                    key={method.id || `method-${index}`}
                    className="hover:bg-indigo-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-indigo-800">
                        #{method.id}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                          <CreditCard size={20} />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800">
                            {method.paymentMethodName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => openEditModal(method)}
                          className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                          title="Edit"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => deletePaymentMethod(method)}
                          className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <CreditCard size={48} />
                      <p className="text-lg font-medium">
                        No payment methods found
                      </p>
                      <p className="text-sm">
                        Try adjusting your search or create a new payment method
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {paginated.length > 0 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                Showing {(page - 1) * perPage + 1} to{" "}
                {Math.min(page * perPage, filtered.length)} of{" "}
                {filtered.length} payment methods
              </span>
              <div className="flex items-center gap-3">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm font-semibold text-gray-700">
                  Page {page} of {totalPages || 1}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MODAL */}
      {(openCreate || openEdit) && (
        <Modal
          title={openCreate ? "Create Payment Method" : "Edit Payment Method"}
          close={() => {
            setOpenCreate(false);
            setOpenEdit(false);
            setSelectedMethod(null);
            clearForm();
          }}
        >
          <div className="space-y-4">
            <Input
              label="Payment Method Name*"
              value={formData.paymentMethodName}
              onChange={(val) =>
                setFormData({ ...formData, paymentMethodName: val })
              }
              required
              placeholder="e.g., PA - Promo Allowance"
            />
          </div>

          <button
            onClick={openCreate ? createPaymentMethod : updatePaymentMethod}
            className="mt-6 w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          >
            {openCreate ? "Create Payment Method" : "Update Payment Method"}
          </button>
        </Modal>
      )}
    </div>
  );
}

// Modal Component
function Modal({ title, close, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={close}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition"
        >
          <X size={24} />
        </button>
        <h2 className="text-3xl font-bold text-gray-800 mb-8">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
      />
    </div>
  );
}